import { BitmapText, Container, Graphics } from "pixi.js";
import type { PresentationTaskRunner } from "@slot/presentation";
import { BITMAP_FONTS, setCoinText } from "../views/textStyles";
import { FX_SCOPE } from "./fxScope";

/** The plaque: a dark red box with a gold rim over the respin meter's place, its amount in the coins' digits (design units). */
const PLAQUE = { width: 400, height: 104, radius: 26, rim: 7, amountSize: 66, amountBox: 350 } as const;

/**
 * The bonus's total while its board pays, as 3 Coin Volcanoes' win meter: a plaque in the respin meter's place under the
 * grid (the meter gives way to it). It opens empty; each cell's amount arrives on a comet and it steps up to the running
 * sum, swelling, then to the round's total. Hidden outside the end of the bonus.
 */
export class BonusTotalView extends Container {
  private readonly amount: BitmapText;
  private readonly format: (minor: string) => string;
  private readonly runner: PresentationTaskRunner;
  private shownMinor = 0n;

  public constructor(input: { format: (minor: string) => string; runner: PresentationTaskRunner }) {
    super({ label: "BonusTotal" });
    this.format = input.format;
    this.runner = input.runner;
    const box = new Graphics()
      .roundRect(-PLAQUE.width / 2, -PLAQUE.height / 2, PLAQUE.width, PLAQUE.height, PLAQUE.radius)
      .fill({ color: 0x5a0408, alpha: 0.96 })
      .stroke({ color: 0xffc83a, width: PLAQUE.rim });
    this.amount = new BitmapText({ text: "", style: { fontFamily: BITMAP_FONTS.coin, fontSize: PLAQUE.amountSize }, label: "Amount" });
    this.amount.anchor.set(0.5);
    this.addChild(box, this.amount);
    this.visible = false;
  }

  /** Opens empty (the plaque pops in), to count from nothing. */
  public async open(seconds: number): Promise<void> {
    this.shownMinor = 0n;
    this.amount.text = "";
    this.visible = true;
    await this.runner.tweenProgress(seconds, (k) => {
      if (!this.destroyed) this.scale.set(0.4 + 0.6 * k, 0.4 + 0.6 * k);
    }, FX_SCOPE, { easing: "backOut" });
  }

  /** Counts from what it shows to `minor` over `seconds`, swelling as it goes. */
  public async countTo(minor: bigint, seconds: number): Promise<void> {
    const from = this.shownMinor;
    await this.runner.tweenProgress(seconds, (k) => {
      if (this.destroyed) return;
      this.setAmount(from + ((minor - from) * BigInt(Math.round(k * 1000))) / 1000n);
      const s = 1 + 0.12 * Math.sin(Math.PI * k);
      this.amount.scale.set(s * this.fit(), s * this.fit());
    }, FX_SCOPE, { easing: "quadOut" });
    this.setAmount(minor);
  }

  public close(): void {
    this.visible = false;
    this.shownMinor = 0n;
  }

  private setAmount(minor: bigint): void {
    this.shownMinor = minor;
    setCoinText(this.amount, this.format(minor.toString()), PLAQUE.amountSize);
    this.amount.scale.set(this.fit(), this.fit());
  }

  private fit(): number {
    const natural = this.amount.width / (this.amount.scale.x || 1);
    return Math.min(1, PLAQUE.amountBox / Math.max(1, natural));
  }
}
